import React from 'react';
import { motion } from 'framer-motion';

/**
 * Footer component with disclaimer, copyright, and links
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const footerLinks = {
    product: [
      { name: 'Token Details', href: '#token-details' },
      { name: 'Roadmap', href: '#roadmap' },
      { name: 'About', href: '#about' },
      { name: 'Etherscan', href: 'https://etherscan.io/token/0xd043f70ff0634167db1149b15e5668f5be52d72c' }
    ],
    community: [
      { name: 'Twitter', href: '#' },
      { name: 'Telegram', href: '#' },
      { name: 'Discord', href: '#' },
      { name: 'Medium', href: '#' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '#' },
      { name: 'Terms of Service', href: '#' },
      { name: 'Cookie Policy', href: '#' },
      { name: 'Disclaimer', href: '#disclaimer' }
    ]
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };
  
  return (
    <footer className="bg-gradient-to-br from-coffee-800 to-coffee-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 text-6xl">☕</div>
        <div className="absolute top-20 right-20 text-4xl">🫘</div>
        <div className="absolute bottom-20 left-20 text-5xl">☕</div>
        <div className="absolute bottom-10 right-10 text-3xl">🫘</div>
      </div>
      
      <div className="relative z-10">
        {/* Main Footer Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="container-custom py-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand Section */}
            <motion.div variants={itemVariants} className="lg:col-span-1">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cream-300 to-cream-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-coffee-800 font-bold text-lg">BGC</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold font-display">BigCash</h3>
                  <p className="text-coffee-200 text-sm">Coffee Token</p>
                </div>
              </div>
              <p className="text-coffee-200 mb-6 leading-relaxed">
                Malaysia's first coffee token, bridging traditional coffee culture with blockchain innovation.
              </p>
              <div className="flex space-x-4">
                <span className="text-2xl">☕</span>
                <span className="text-2xl">🇲🇾</span>
                <span className="text-2xl">🚀</span>
              </div>
            </motion.div>
            
            {/* Product Links */}
            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold mb-6 text-cream-100">Product</h4>
              <ul className="space-y-3">
                {footerLinks.product.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-coffee-200 hover:text-cream-100 transition-colors duration-300 text-sm"
                      target={link.href.startsWith('http') ? '_blank' : '_self'}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            {/* Community Links */}
            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold mb-6 text-cream-100">Community</h4>
              <ul className="space-y-3">
                {footerLinks.community.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-coffee-200 hover:text-cream-100 transition-colors duration-300 text-sm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            {/* Legal Links */}
            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold mb-6 text-cream-100">Legal</h4>
              <ul className="space-y-3">
                {footerLinks.legal.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-coffee-200 hover:text-cream-100 transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>
        
        {/* Disclaimer Section */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="border-t border-coffee-700"
        >
          <div className="container-custom py-8">
            <div className="bg-coffee-700/50 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold mb-4 text-cream-100 flex items-center">
                <span className="mr-2">⚠️</span>
                Important Disclaimer
              </h4>
              <p className="text-coffee-200 text-sm leading-relaxed mb-4">
                <strong>Cryptocurrency investment carries risks. Please do your own research.</strong> 
                BigCash Coffee Token (BGC) is a digital asset and its value can fluctuate significantly. 
                Past performance does not guarantee future results. Only invest what you can afford to lose.
              </p>
              <p className="text-coffee-200 text-sm leading-relaxed">
                This website and its content are for informational purposes only and do not constitute 
                financial advice, investment recommendations, or an offer to buy or sell securities. 
                Always consult with qualified financial advisors before making investment decisions.
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Bottom Bar */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="border-t border-coffee-700"
        >
          <div className="container-custom py-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-coffee-200 text-sm">
                © {currentYear} BigCash Coffee Token. All rights reserved.
              </div>
              
              <div className="flex items-center space-x-6 text-sm text-coffee-200">
                <span>Contract: 0xd043f70f...5be52d72c</span>
                <a
                  href="https://etherscan.io/token/0xd043f70ff0634167db1149b15e5668f5be52d72c"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cream-100 transition-colors duration-300"
                >
                  Verify on Etherscan
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;